import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function LoansPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  const products = [
    {
      id: 'salary',
      title: 'Salary-Based Loans',
      image: 'https://public.readdy.ai/ai/img_res/a94de2f64a1d12c2cd6d366e38b70833.jpg',
      whoFor: [
        'Salaried employees with regular income',
        'Government and private sector workers',
        'Individuals with verifiable employment'
      ],
      useCases: [
        { icon: 'ri-home-line', text: 'Home improvements and repairs' },
        { icon: 'ri-graduation-cap-line', text: 'Education and training expenses' },
        { icon: 'ri-hospital-line', text: 'Medical emergencies' },
        { icon: 'ri-shopping-bag-line', text: 'Personal and family needs' }
      ],
      benefits: [
        'Flexible repayment terms aligned with salary cycles',
        'Competitive rates based on employment stability',
        'Quick processing with minimal documentation',
        'No collateral required for qualifying applicants'
      ]
    },
    {
      id: 'sme',
      title: 'SME & Working Capital Loans',
      image: 'https://readdy.ai/api/search-image?query=African%20small%20business%20owner%20in%20their%20shop%20or%20workshop%20managing%20inventory%20and%20operations%2C%20entrepreneur%20working%20with%20products%20and%20equipment%2C%20clean%20organized%20business%20environment%20with%20simple%20background%2C%20professional%20photography%20showing%20business%20growth%20and%20success&width=600&height=400&seq=loan-sme-002&orientation=landscape',
      whoFor: [
        'Small and medium enterprises',
        'Registered businesses with trading history',
        'Companies seeking growth capital'
      ],
      useCases: [
        { icon: 'ri-box-line', text: 'Inventory and stock purchases' },
        { icon: 'ri-tools-line', text: 'Equipment and machinery' },
        { icon: 'ri-team-line', text: 'Staff salaries and operations' },
        { icon: 'ri-line-chart-line', text: 'Business expansion projects' }
      ],
      benefits: [
        'Tailored to your business cash flow cycles',
        'Flexible loan amounts based on business needs',
        'Dedicated business loan officers',
        'Support for seasonal and growth financing'
      ]
    },
    {
      id: 'microenterprise',
      title: 'Microenterprise Loans',
      image: 'https://readdy.ai/api/search-image?query=African%20informal%20sector%20entrepreneur%20running%20small%20market%20stall%20or%20street%20business%2C%20microenterprise%20owner%20with%20their%20products%20in%20simple%20clean%20setting%2C%20warm%20natural%20lighting%20showing%20determination%20and%20entrepreneurial%20spirit%2C%20minimal%20background%20focus%20on%20business%20owner&width=600&height=400&seq=loan-micro-003&orientation=landscape',
      whoFor: [
        'Informal sector entrepreneurs',
        'Market vendors and traders',
        'Home-based business owners'
      ],
      useCases: [
        { icon: 'ri-store-2-line', text: 'Starting or expanding small businesses' },
        { icon: 'ri-shopping-cart-line', text: 'Purchasing trading stock' },
        { icon: 'ri-hammer-line', text: 'Tools and basic equipment' },
        { icon: 'ri-truck-line', text: 'Transport and logistics needs' }
      ],
      benefits: [
        'Accessible to informal sector businesses',
        'Small loan amounts with manageable repayments',
        'Simplified application process',
        'Financial literacy support and guidance'
      ]
    },
    {
      id: 'asset',
      title: 'Asset-Backed Loans',
      image: 'https://readdy.ai/api/search-image?query=Valuable%20assets%20including%20property%20documents%2C%20vehicle%2C%20and%20business%20equipment%20arranged%20professionally%2C%20clean%20modern%20setting%20showing%20security%20and%20value%2C%20professional%20photography%20with%20simple%20neutral%20background%20emphasizing%20trust%20and%20collateral%20security&width=600&height=400&seq=loan-asset-004&orientation=landscape',
      whoFor: [
        'Property and vehicle owners',
        'Businesses with valuable assets',
        'Individuals seeking larger loan amounts'
      ],
      useCases: [
        { icon: 'ri-building-line', text: 'Property development and renovation' },
        { icon: 'ri-car-line', text: 'Vehicle purchase or upgrade' },
        { icon: 'ri-funds-line', text: 'Debt consolidation' },
        { icon: 'ri-exchange-line', text: 'Major investments and projects' }
      ],
      benefits: [
        'Higher loan amounts based on asset value',
        'Favorable interest rates with collateral',
        'Longer repayment periods available',
        'Professional asset valuation services'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Banner */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-[#1A3A52]">
        <div className="max-w-4xl mx-auto text-center">
          <nav className="text-sm text-white/60 mb-4">
            <span>Home</span> <span className="mx-2">/</span> <span className="text-white">Loan Products</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Our Loan Products
          </h1>
          <p className="text-lg text-white/80">
            Flexible financing solutions designed for your unique needs
          </p>
        </div>
      </section>

      {/* Product Sections */}
      {products.map((product, index) => (
        <section 
          key={product.id}
          id={product.id}
          className={`py-20 px-4 sm:px-6 lg:px-8 ${index % 2 === 0 ? 'bg-white' : 'bg-[#E8EFF5]'}`}
        >
          <div className="max-w-7xl mx-auto">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              {/* Image */}
              <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="rounded-xl overflow-hidden shadow-lg">
                  <div className="w-full h-80">
                    <img 
                      src={product.image}
                      alt={product.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className={`space-y-6 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <h2 className="text-3xl md:text-4xl font-bold text-[#1A3A52]">
                  {product.title}
                </h2>

                {/* Who This Is For */}
                <div>
                  <h3 className="text-xl font-bold text-[#1A3A52] mb-3">Who This Is For</h3>
                  <ul className="space-y-2">
                    {product.whoFor.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-700">
                        <i className="ri-check-line text-[#14B8A6] mt-1"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Typical Use Cases */}
                <div>
                  <h3 className="text-xl font-bold text-[#1A3A52] mb-3">Typical Use Cases</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {product.useCases.map((useCase, i) => (
                      <div key={i} className="flex items-center gap-2 text-gray-700">
                        <i className={`${useCase.icon} text-[#1A3A52]`}></i>
                        <span className="text-sm">{useCase.text}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Key Benefits */}
                <div>
                  <h3 className="text-xl font-bold text-[#1A3A52] mb-3">Key Benefits</h3>
                  <div className="space-y-2">
                    {product.benefits.map((benefit, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <div className="w-6 h-6 flex items-center justify-center bg-[#14B8A6] rounded-full flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-white text-sm"></i>
                        </div>
                        <span className="text-gray-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Link to="/apply">
                    <Button variant="primary" size="medium">
                      Apply Now
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button variant="outline" size="medium">
                      Speak to a Loan Officer
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Pricing Disclaimer */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50 border-y border-gray-200">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm text-gray-600">
            <i className="ri-information-line text-[#1A3A52] mr-2"></i>
            All loan pricing is fair, transparent, and based on individual risk assessment. Terms and conditions apply to all products.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}